import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Download, Trash2, Check, X, Users, Baby, Mail, Send, RefreshCw, Home, TreePine, Tent, Euro, Edit, Save, Coffee } from 'lucide-react';
import { AdminLayout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import api, { getAllRSVPs, deleteRSVP, exportCSV, sendReminder, confirmReception, getAdminMe } from '@/lib/api';

// Prix par type de logement
const LODGING_PRICES = {
  chambre: 50,
  cabane: 35,
  tente: 30,
  tente_perso: 0
};

const LODGING_LABELS = {
  chambre: { label: 'Chambre', icon: Home, color: 'text-blue-600' },
  cabane: { label: 'Cabane', icon: TreePine, color: 'text-green-600' },
  tente: { label: 'Tente cloche', icon: Tent, color: 'text-amber-600' },
  tente_perso: { label: 'Tente perso', icon: Tent, color: 'text-emerald-600' }
};

const AdminRSVPs = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rsvps, setRsvps] = useState([]);
  const [filteredRsvps, setFilteredRsvps] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAttending, setFilterAttending] = useState('all');
  const [filterAccommodation, setFilterAccommodation] = useState('all');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, rsvp: null });
  const [actionLoading, setActionLoading] = useState(null);
  const [editDialog, setEditDialog] = useState({ open: false, rsvp: null });
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterData();
  }, [rsvps, searchTerm, filterAttending, filterAccommodation]);

  const loadData = async () => {
    try {
      await getAdminMe();
      const response = await getAllRSVPs();
      setRsvps(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const filterData = () => {
    let filtered = [...rsvps];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.family_name?.toLowerCase().includes(term) ||
          r.email?.toLowerCase().includes(term)
      );
    }

    if (filterAttending !== 'all') {
      filtered = filtered.filter((r) => r.attending === (filterAttending === 'yes'));
    }

    if (filterAccommodation !== 'all') {
      filtered = filtered.filter((r) => {
        const accType = r.accommodation?.accommodation_type;
        if (filterAccommodation === 'none') return !accType || accType === 'none';
        return accType === filterAccommodation;
      });
    }

    setFilteredRsvps(filtered);
  };

  const handleDelete = async () => {
    if (!deleteDialog.rsvp) return;
    try {
      await deleteRSVP(deleteDialog.rsvp.id);
      toast.success('Confirmation supprimée');
      setDeleteDialog({ open: false, rsvp: null });
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSendReminder = async (rsvp) => {
    setActionLoading(rsvp.id);
    try {
      await sendReminder(rsvp.id);
      toast.success(`Relance envoyée à ${rsvp.email}`);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi de la relance');
    } finally {
      setActionLoading(null);
    }
  };

  const handleConfirmReception = async (rsvp) => {
    setActionLoading(rsvp.id);
    try {
      await confirmReception(rsvp.id);
      toast.success(`Confirmation envoyée à ${rsvp.email}`);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setActionLoading(null);
    }
  };

  const handleExport = async () => {
    try {
      const response = await exportCSV();
      // Add BOM for Excel UTF-8 compatibility
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + response.data.csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invites_mariage_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('Export téléchargé ! Ouvrez avec Excel.');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const openEditDialog = (rsvp) => {
    setEditForm({
      family_name: rsvp.family_name || '',
      email: rsvp.email || '',
      phone: rsvp.phone || '',
      attending: rsvp.attending,
      adults: rsvp.adults || [],
      children: rsvp.children || [],
      dietary_restrictions: rsvp.dietary_restrictions || '',
      message_to_couple: rsvp.message_to_couple || '',
      absence_message: rsvp.absence_message || '',
      brunch_attending: rsvp.brunch_attending,
      brunch_count: rsvp.brunch_count || 0,
      // Accommodation data
      accommodation_type: rsvp.accommodation?.accommodation_type || 'none',
      preferred_lodging: rsvp.accommodation?.preferred_lodging || '',
      assigned_lodging: rsvp.accommodation?.assigned_lodging || '',
      accommodation_status: rsvp.accommodation?.status || 'pending',
      accommodation_notes: rsvp.accommodation?.notes || '',
    });
    setEditDialog({ open: true, rsvp });
  };

  const handleSaveEdit = async () => {
    if (!editDialog.rsvp) return;
    setSaving(true);
    
    try {
      // Update RSVP data
      const rsvpData = {
        family_name: editForm.family_name,
        email: editForm.email,
        phone: editForm.phone,
        attending: editForm.attending,
        adults: editForm.adults,
        children: editForm.children,
        dietary_restrictions: editForm.dietary_restrictions,
        message_to_couple: editForm.message_to_couple,
        absence_message: editForm.absence_message,
        brunch_attending: editForm.brunch_attending,
        brunch_count: editForm.brunch_count,
      };
      
      console.log('Saving RSVP:', editDialog.rsvp.id, rsvpData);
      await api.put(`/admin/rsvp/${editDialog.rsvp.id}`, rsvpData);
      
      // Handle accommodation
      const accData = {
        accommodation_type: editForm.accommodation_type || 'none',
        preferred_lodging: editForm.preferred_lodging || '',
        assigned_lodging: editForm.assigned_lodging || '',
        status: editForm.accommodation_status || 'pending',
        notes: editForm.accommodation_notes || '',
        num_adults: editForm.adults.length,
        num_children: editForm.children.length,
      };
      
      // Only process accommodation if type is not 'none'
      if (accData.accommodation_type && accData.accommodation_type !== 'none') {
        if (editDialog.rsvp.accommodation?.id) {
          // Update existing accommodation
          console.log('Updating accommodation:', editDialog.rsvp.accommodation.id, accData);
          await api.put(`/admin/accommodation/${editDialog.rsvp.accommodation.id}`, accData);
        } else {
          // Create new accommodation
          console.log('Creating new accommodation for RSVP:', editDialog.rsvp.id, accData);
          await api.post('/admin/accommodation', {
            rsvp_id: editDialog.rsvp.id,
            ...accData
          });
        }
      }
      
      toast.success('Modifications enregistrées');
      setEditDialog({ open: false, rsvp: null });
      loadData();
    } catch (error) {
      console.error('Save error:', error.response?.data || error.message);
      toast.error(`Erreur: ${error.response?.data?.detail || error.message || 'Erreur lors de la sauvegarde'}`);
    } finally {
      setSaving(false);
    }
  };

  const updateAdult = (index, field, value) => {
    const newAdults = [...editForm.adults];
    newAdults[index] = { ...newAdults[index], [field]: value };
    setEditForm({ ...editForm, adults: newAdults });
  };

  const addAdult = () => {
    setEditForm({
      ...editForm,
      adults: [...editForm.adults, { civility: 'Monsieur', first_name: '', last_name: '' }]
    });
  };

  const removeAdult = (index) => {
    if (editForm.adults.length <= 1) return;
    const newAdults = editForm.adults.filter((_, i) => i !== index);
    setEditForm({ ...editForm, adults: newAdults });
  };

  const updateChild = (index, field, value) => {
    const newChildren = [...editForm.children];
    newChildren[index] = { ...newChildren[index], [field]: value };
    setEditForm({ ...editForm, children: newChildren });
  };

  const addChild = () => {
    setEditForm({
      ...editForm,
      children: [...editForm.children, { first_name: '', age: '' }]
    });
  };

  const removeChild = (index) => {
    const newChildren = editForm.children.filter((_, i) => i !== index);
    setEditForm({ ...editForm, children: newChildren });
  };

  if (loading) {
    return (
      <AdminLayout title="Gestion des invités">
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-gray-500">Chargement...</div>
        </div>
      </AdminLayout>
    );
  }

  // Stats
  const totalAdults = rsvps.filter(r => r.attending).reduce((sum, r) => sum + (r.adults?.length || 0), 0);
  const totalChildren = rsvps.filter(r => r.attending).reduce((sum, r) => sum + (r.children?.length || 0), 0);

  return (
    <AdminLayout title="Gestion des invités">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
          <p className="text-2xl font-bold text-gray-900">{rsvps.length}</p>
          <p className="text-sm text-gray-500">Réponses</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{rsvps.filter(r => r.attending).length}</p>
          <p className="text-sm text-gray-500">Présents</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
          <p className="text-2xl font-bold text-gray-900">{totalAdults}</p>
          <p className="text-sm text-gray-500">Adultes</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
          <p className="text-2xl font-bold text-gray-900">{totalChildren}</p>
          <p className="text-sm text-gray-500">Enfants</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                data-testid="search-input"
                placeholder="Rechercher par nom ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <Select value={filterAttending} onValueChange={setFilterAttending}>
            <SelectTrigger data-testid="filter-attending" className="w-full md:w-40">
              <SelectValue placeholder="Présence" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="yes">Présents</SelectItem>
              <SelectItem value="no">Absents</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterAccommodation} onValueChange={setFilterAccommodation}>
            <SelectTrigger data-testid="filter-accommodation" className="w-full md:w-40">
              <SelectValue placeholder="Hébergement" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="domaine">Domaine</SelectItem>
              <SelectItem value="hotel">Hôtel</SelectItem>
              <SelectItem value="none">Aucun</SelectItem>
            </SelectContent>
          </Select>
          <Button data-testid="btn-export" onClick={handleExport} variant="outline" className="gap-2">
            <Download className="w-4 h-4" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Famille</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="text-center">Présence</TableHead>
                <TableHead className="text-center">Adultes</TableHead>
                <TableHead className="text-center">Enfants</TableHead>
                <TableHead>Hébergement</TableHead>
                <TableHead>Préférence</TableHead>
                <TableHead className="text-right">Montant</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRsvps.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                    Aucun invité trouvé
                  </TableCell>
                </TableRow>
              ) : (
                filteredRsvps.map((rsvp) => (
                  <TableRow key={rsvp.id}>
                    <TableCell className="font-medium">{rsvp.family_name}</TableCell>
                    <TableCell className="text-sm text-gray-500">{rsvp.email}</TableCell>
                    <TableCell className="text-center">
                      {rsvp.attending ? (
                        <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                          <Check className="w-3 h-3 mr-1" /> Oui
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-red-100 text-red-800 hover:bg-red-100">
                          <X className="w-3 h-3 mr-1" /> Non
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="flex items-center justify-center gap-1">
                        <Users className="w-4 h-4 text-gray-400" />
                        {rsvp.adults?.length || 0}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="flex items-center justify-center gap-1">
                        <Baby className="w-4 h-4 text-gray-400" />
                        {rsvp.children?.length || 0}
                      </span>
                    </TableCell>
                    <TableCell>
                      {rsvp.accommodation && rsvp.accommodation.accommodation_type !== 'none' ? (
                        <div className="flex flex-col gap-1">
                          <Badge variant="outline">
                            {rsvp.accommodation.accommodation_type === 'domaine' ? 'Domaine' : 'Hôtel'}
                          </Badge>
                          {rsvp.accommodation.accommodation_type === 'domaine' && (
                            <span className={`text-xs ${
                              rsvp.accommodation.status === 'confirmed' ? 'text-green-600' :
                              rsvp.accommodation.status === 'pending' ? 'text-amber-600' : 'text-gray-500'
                            }`}>
                              {rsvp.accommodation.status === 'confirmed' ? '✓ Confirmé' :
                               rsvp.accommodation.status === 'pending' ? '⏳ En attente' : rsvp.accommodation.status}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const preferred = rsvp.accommodation?.preferred_lodging;
                        const assigned = rsvp.accommodation?.assigned_lodging;
                        const lodgingKey = assigned || preferred;
                        
                        if (lodgingKey && LODGING_LABELS[lodgingKey]) {
                          const lodging = LODGING_LABELS[lodgingKey];
                          const IconComponent = lodging.icon;
                          return (
                            <div className="flex items-center gap-1.5">
                              <IconComponent className={`w-4 h-4 ${lodging.color}`} />
                              <span className={`text-sm ${assigned ? 'font-medium' : ''}`}>
                                {lodging.label}
                              </span>
                              {assigned && <span className="text-xs text-green-600">✓</span>}
                            </div>
                          );
                        }
                        return <span className="text-gray-400 text-sm">-</span>;
                      })()}
                    </TableCell>
                    <TableCell className="text-right">
                      {(() => {
                        const preferred = rsvp.accommodation?.preferred_lodging;
                        const assigned = rsvp.accommodation?.assigned_lodging;
                        const lodgingKey = assigned || preferred;
                        const numAdults = rsvp.adults?.length || 0;
                        
                        if (lodgingKey && LODGING_PRICES[lodgingKey] !== undefined && rsvp.accommodation?.accommodation_type === 'domaine') {
                          const price = LODGING_PRICES[lodgingKey];
                          const total = price * numAdults;
                          
                          if (price === 0) {
                            return <span className="text-emerald-600 font-medium text-sm">Gratuit</span>;
                          }
                          
                          return (
                            <div className="flex flex-col items-end">
                              <span className={`font-medium ${assigned ? 'text-green-600' : 'text-amber-600'}`}>
                                {total}€
                              </span>
                              <span className="text-xs text-gray-500">
                                {price}€ × {numAdults}
                              </span>
                            </div>
                          );
                        }
                        return <span className="text-gray-400 text-sm">-</span>;
                      })()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(rsvp)}
                          className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                          data-testid={`btn-edit-${rsvp.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" disabled={actionLoading === rsvp.id}>
                              {actionLoading === rsvp.id ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <Mail className="w-4 h-4" />
                              )}
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleConfirmReception(rsvp)}>
                              <Check className="w-4 h-4 mr-2" />
                              Confirmer réception
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleSendReminder(rsvp)}>
                              <Send className="w-4 h-4 mr-2" />
                              Envoyer relance
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <Button
                          data-testid={`btn-delete-${rsvp.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteDialog({ open: true, rsvp })}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Results count */}
      <p className="text-sm text-gray-500 mt-4">
        {filteredRsvps.length} résultat(s) sur {rsvps.length}
      </p>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, rsvp: deleteDialog.rsvp })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer cet invité ?</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">
            Êtes-vous sûr de vouloir supprimer <strong>{deleteDialog.rsvp?.family_name}</strong> ?
            Cette action supprimera également les données d'hébergement et de paiement associées.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, rsvp: null })}>
              Annuler
            </Button>
            <Button data-testid="btn-confirm-delete" variant="destructive" onClick={handleDelete}>
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, rsvp: editDialog.rsvp })}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-500" />
              Modifier l'inscription - {editDialog.rsvp?.family_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Informations générales */}
            <div className="space-y-4">
              <h3 className="font-semibold text-charcoal border-b pb-2">Informations générales</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Nom de famille</Label>
                  <Input
                    value={editForm.family_name || ''}
                    onChange={(e) => setEditForm({ ...editForm, family_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editForm.email || ''}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Téléphone</Label>
                  <Input
                    value={editForm.phone || ''}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Présence</Label>
                  <Select
                    value={editForm.attending ? 'yes' : 'no'}
                    onValueChange={(v) => setEditForm({ ...editForm, attending: v === 'yes' })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Présent</SelectItem>
                      <SelectItem value="no">Absent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Adultes */}
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b pb-2">
                <h3 className="font-semibold text-charcoal">Adultes ({editForm.adults?.length || 0})</h3>
                <Button type="button" variant="outline" size="sm" onClick={addAdult}>
                  + Ajouter
                </Button>
              </div>
              {editForm.adults?.map((adult, idx) => (
                <div key={idx} className="flex flex-wrap items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <Select
                    value={adult.civility || 'Monsieur'}
                    onValueChange={(v) => updateAdult(idx, 'civility', v)}
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Monsieur">M.</SelectItem>
                      <SelectItem value="Madame">Mme</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Prénom"
                    value={adult.first_name || ''}
                    onChange={(e) => updateAdult(idx, 'first_name', e.target.value)}
                    className="flex-1 min-w-[110px]"
                  />
                  <Input
                    placeholder="Nom"
                    value={adult.last_name || ''}
                    onChange={(e) => updateAdult(idx, 'last_name', e.target.value)}
                    className="flex-1 min-w-[110px]"
                  />
                  {editForm.adults.length > 1 && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeAdult(idx)} className="text-red-500">
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {/* Enfants */}
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b pb-2">
                <h3 className="font-semibold text-charcoal">Enfants ({editForm.children?.length || 0})</h3>
                <Button type="button" variant="outline" size="sm" onClick={addChild}>
                  + Ajouter
                </Button>
              </div>
              {editForm.children?.map((child, idx) => (
                <div key={idx} className="flex flex-wrap items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <Input
                    placeholder="Prénom"
                    value={child.first_name || ''}
                    onChange={(e) => updateChild(idx, 'first_name', e.target.value)}
                    className="flex-1 min-w-[110px]"
                  />
                  <Input
                    placeholder="Âge"
                    type="number"
                    value={child.age || ''}
                    onChange={(e) => updateChild(idx, 'age', parseInt(e.target.value) || '')}
                    className="w-20"
                  />
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeChild(idx)} className="text-red-500">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              {(!editForm.children || editForm.children.length === 0) && (
                <p className="text-gray-400 text-sm">Aucun enfant</p>
              )}
            </div>

            {/* Brunch */}
            <div className="space-y-4">
              <h3 className="font-semibold text-charcoal border-b pb-2 flex items-center gap-2">
                <Coffee className="w-4 h-4" /> Brunch du dimanche
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Présence au brunch</Label>
                  <Select
                    value={editForm.brunch_attending === true ? 'yes' : editForm.brunch_attending === false ? 'no' : 'pending'}
                    onValueChange={(v) => setEditForm({ 
                      ...editForm, 
                      brunch_attending: v === 'yes' ? true : v === 'no' ? false : null 
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Oui</SelectItem>
                      <SelectItem value="no">Non</SelectItem>
                      <SelectItem value="pending">Non renseigné</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {editForm.brunch_attending === true && (
                  <div>
                    <Label>Nombre de personnes</Label>
                    <Input
                      type="number"
                      min="1"
                      value={editForm.brunch_count || 0}
                      onChange={(e) => setEditForm({ ...editForm, brunch_count: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Hébergement */}
            <div className="space-y-4">
              <h3 className="font-semibold text-charcoal border-b pb-2">Hébergement</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Type d'hébergement</Label>
                  <Select
                    value={editForm.accommodation_type || 'none'}
                    onValueChange={(v) => setEditForm({ ...editForm, accommodation_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun</SelectItem>
                      <SelectItem value="domaine">Au domaine</SelectItem>
                      <SelectItem value="hotel">Hôtel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Statut</Label>
                  <Select
                    value={editForm.accommodation_status || 'pending'}
                    onValueChange={(v) => setEditForm({ ...editForm, accommodation_status: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">En attente</SelectItem>
                      <SelectItem value="confirmed">Confirmé</SelectItem>
                      <SelectItem value="waitlist">Liste d'attente</SelectItem>
                      <SelectItem value="cancelled">Annulé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Préférence logement</Label>
                  <Select
                    value={editForm.preferred_lodging || 'none'}
                    onValueChange={(v) => setEditForm({ ...editForm, preferred_lodging: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Non défini</SelectItem>
                      <SelectItem value="chambre">Chambre (50€/adulte)</SelectItem>
                      <SelectItem value="cabane">Cabane (35€/adulte)</SelectItem>
                      <SelectItem value="tente">Tente cloche (30€/adulte)</SelectItem>
                      <SelectItem value="tente_perso">Tente perso (gratuit)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Logement attribué</Label>
                  <Select
                    value={editForm.assigned_lodging || 'none'}
                    onValueChange={(v) => setEditForm({ ...editForm, assigned_lodging: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Non attribué</SelectItem>
                      <SelectItem value="chambre">Chambre (50€/adulte)</SelectItem>
                      <SelectItem value="cabane">Cabane (35€/adulte)</SelectItem>
                      <SelectItem value="tente">Tente cloche (30€/adulte)</SelectItem>
                      <SelectItem value="tente_perso">Tente perso (gratuit)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Autres informations */}
            <div className="space-y-4">
              <h3 className="font-semibold text-charcoal border-b pb-2">Autres informations</h3>
              <div>
                <Label>Régimes alimentaires / Allergies</Label>
                <Textarea
                  value={editForm.dietary_restrictions || ''}
                  onChange={(e) => setEditForm({ ...editForm, dietary_restrictions: e.target.value })}
                  rows={2}
                />
              </div>
              <div>
                <Label>Message au couple</Label>
                <Textarea
                  value={editForm.message_to_couple || ''}
                  onChange={(e) => setEditForm({ ...editForm, message_to_couple: e.target.value })}
                  rows={2}
                />
              </div>
              {!editForm.attending && (
                <div>
                  <Label>Message d'absence</Label>
                  <Textarea
                    value={editForm.absence_message || ''}
                    onChange={(e) => setEditForm({ ...editForm, absence_message: e.target.value })}
                    rows={2}
                  />
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, rsvp: null })}>
              Annuler
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving} className="gap-2 bg-terracotta hover:bg-terracotta/90">
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Enregistrer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminRSVPs;
